/* Les quatre documents du parcours, sur la fiche de vente.
 *
 * Le bon de commande du client, celui qu'on passe à l'usine, le départ de la
 * marchandise et son arrivée. Chacun porte ce qui le prouve — un numéro, une
 * date — et c'est ce qui ouvre l'étape suivante : on ne fait pas avancer une
 * vente en cliquant, on la fait avancer en renseignant ce qui s'est passé.
 */

import { useState } from "react";
import { ETAPES } from "@/domain/ventes";
import { Section, Tag } from "@/ui/primitives";
import { Champ } from "@/ui/Drawer";
import { useToast } from "@/ui/Toast";
import { dateFr, aujourdhui } from "@/lib/format";
import { poserBonCommande, poserExpedition, poserLivraison } from "./actions";
import type { ContexteCommission } from "./actions";
import type { Vente } from "@/types/metier";

type Jalon = "bdc_client" | "bdc_usine" | "expedition" | "livraison";

const DEFS: { cle: Jalon; titre: string; note: string; etape: string; numero: string | null }[] = [
  { cle: "bdc_client", titre: "Commande du client", etape: "commande_client",
    note: "reçue du client, elle porte SON numéro", numero: "Numéro du client" },
  { cle: "bdc_usine", titre: "Commande à l'usine", etape: "commande_usine",
    note: "envoyée à l'usine, elle porte le nôtre", numero: "Notre numéro" },
  { cle: "expedition", titre: "Expédition", etape: "expedie",
    note: "le départ de la marchandise", numero: "Transporteur" },
  { cle: "livraison", titre: "Livraison", etape: "livre",
    note: "l'arrivée chez le client", numero: null },
];

export function Jalons({
  vente, ctx, modifiable, onFait,
}: {
  vente: Vente;
  ctx: ContexteCommission;
  modifiable: boolean;
  onFait: () => void;
}) {
  const toast = useToast();
  const [ouvert, setOuvert] = useState<Jalon | null>(null);
  const [numero, setNumero] = useState("");
  const [date, setDate] = useState(aujourdhui());
  const [note, setNote] = useState("");
  const [occupe, setOccupe] = useState(false);

  const rang = (e: string) => ETAPES.findIndex((x) => x.cle === e);
  const atteinte = rang(vente.etape);

  function ouvrir(d: (typeof DEFS)[number]) {
    setOuvert(d.cle);
    const pose = vente[d.cle];
    setNumero(
      d.cle === "expedition" ? (vente.expedition?.transporteur ?? "")
        : d.cle === "livraison" ? ""
        : ((pose as { numero?: string } | null)?.numero ?? ""),
    );
    setDate((pose as { date?: string } | null)?.date ?? aujourdhui());
    setNote(
      d.cle === "livraison" ? (vente.livraison?.reserves ?? "")
        : d.cle === "expedition" ? (vente.expedition?.document ?? "")
        : "",
    );
  }

  async function poser(cle: Jalon) {
    if (!date) {
      toast("La date manque.");
      return;
    }
    setOccupe(true);
    try {
      if (cle === "bdc_client" || cle === "bdc_usine") {
        if (!numero.trim()) {
          toast("Le numéro manque : c'est lui qui prouve la commande.");
          return;
        }
        await poserBonCommande(vente, cle === "bdc_client" ? "client" : "usine",
                               { numero: numero.trim(), date }, ctx);
      } else if (cle === "expedition") {
        await poserExpedition(vente,
          { date, transporteur: numero.trim() || null, document: note.trim() || null }, ctx);
      } else {
        await poserLivraison(vente, { date, reserves: note.trim() || null }, ctx);
      }
      toast("Enregistré.");
      setOuvert(null);
      onFait();
    } catch (e) {
      toast(e instanceof Error ? e.message : "L'enregistrement a échoué.");
    } finally {
      setOccupe(false);
    }
  }

  return (
    <Section titre="Parcours" note="chaque document ouvre l'étape suivante">
      <div className="jalons">
        {DEFS.map((d) => {
          const pose = vente[d.cle] as
            { numero?: string; date?: string; transporteur?: string | null;
              reserves?: string | null; document?: string | null } | null;
          const franchie = atteinte >= rang(d.etape);
          const ton = ETAPES[rang(d.etape)]?.ton ?? "";
          return (
            <div className={`jalon${pose ? " fait" : ""}`} key={d.cle}>
              <div className="jalon-h">
                <span className="jalon-t">{d.titre}</span>
                {pose ? (
                  <Tag ton={ton} dot>{dateFr(pose.date ?? "")}</Tag>
                ) : franchie ? (
                  <Tag ton="warn">à renseigner</Tag>
                ) : (
                  <Tag>à venir</Tag>
                )}
              </div>
              <div className="jalon-n">{d.note}</div>

              {pose ? (
                <div className="jalon-v">
                  {pose.numero ? <b className="mono">{pose.numero}</b> : null}
                  {pose.transporteur ? <span>{pose.transporteur}</span> : null}
                  {pose.document ? <span className="mono">{pose.document}</span> : null}
                  {pose.reserves ? <span className="jalon-res">{pose.reserves}</span> : null}
                  {!pose.numero && !pose.transporteur && !pose.reserves && !pose.document ? (
                    <span className="locked">sans mention</span>
                  ) : null}
                </div>
              ) : null}

              {modifiable && ouvert !== d.cle ? (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => ouvrir(d)}>
                  {pose ? "Corriger" : "Renseigner"}
                </button>
              ) : null}

              {ouvert === d.cle ? (
                <div className="jalon-form">
                  {d.numero ? (
                    <Champ id={`j-n-${d.cle}`} label={d.numero}>
                      <input
                        id={`j-n-${d.cle}`}
                        value={numero}
                        onChange={(e) => setNumero(e.target.value)}
                      />
                    </Champ>
                  ) : null}
                  <Champ id={`j-d-${d.cle}`} label="Date">
                    <input
                      id={`j-d-${d.cle}`}
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                  </Champ>
                  {d.cle === "expedition" || d.cle === "livraison" ? (
                    <Champ
                      id={`j-r-${d.cle}`}
                      label={d.cle === "livraison" ? "Réserves" : "Document de transport"}
                    >
                      <input
                        id={`j-r-${d.cle}`}
                        value={note}
                        placeholder={d.cle === "livraison" ? "facultatif" : "CMR, LTA…"}
                        onChange={(e) => setNote(e.target.value)}
                      />
                    </Champ>
                  ) : null}
                  <div className="jalon-actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setOuvert(null)}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={occupe}
                      onClick={() => void poser(d.cle)}
                    >
                      Enregistrer
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </Section>
  );
}
